import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play } from "lucide-react";
import { motion } from "framer-motion";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "reel", label: "Reel Social" },
  { key: "promo", label: "Promozionali" },
  { key: "eventi", label: "Eventi" },
  { key: "interviste", label: "Interviste" },
];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    let mounted = true;
    api
      .get(`/portfolio${active !== "all" ? `?category=${active}` : ""}`)
      .then((r) => mounted && setItems(r.data))
      .catch(() => setItems([]));
    return () => {
      mounted = false;
    };
  }, [active]);

  return (
    <section
      id="lavori"
      data-testid="portfolio-section"
      className="relative py-24 sm:py-32 noise-overlay"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <div className="overline mb-4">Lavori selezionati</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter max-w-2xl">
              Alcuni progetti recenti
              <span className="text-white/40"> per clienti locali.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`portfolio-filter-${f.key}`}
                onClick={() => setActive(f.key)}
                className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full border transition-all ${
                  active === f.key
                    ? "bg-gold text-graphite-950 border-gold"
                    : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[240px] md:auto-rows-[280px]">
          {items.map((it, i) => (
            <motion.article
              key={it.id}
              data-testid={`portfolio-item-${it.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`relative group overflow-hidden rounded-xl border border-white/10 hover:border-white/25 transition-all ${
                it.span || "md:col-span-4"
              }`}
            >
              <img
                src={it.image_url}
                alt={it.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-graphite-950/95 via-graphite-950/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <div className="overline mb-2 opacity-80">
                  {it.category} {it.year && `· ${it.year}`}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium leading-tight">
                  {it.title}
                </h3>
                {it.client && <span className="text-white/50 text-sm mt-1">{it.client}</span>}
              </div>
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gold text-graphite-950 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Play strokeWidth={2} className="w-4 h-4 fill-graphite-950 translate-x-0.5" />
              </div>
            </motion.article>
          ))}
          {items.length === 0 && (
            <div className="md:col-span-12 text-center text-white/40 py-20">
              Nessun lavoro in questa categoria.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
